import React from "react";
import type { Step } from "react-joyride";
import { MRT_ColumnDef } from "material-react-table";
import { Box, Button, LinearProgress, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { EsgFileIcon, NewTabIcon } from "assets";
import {
  determineAlgorithmName,
  getDateFormatset,
  getDotColor,
  getInitials,
  getStatusLabel,
  isNullOrUndefined,
} from "utils";
import { COLORS, Avatar } from "components";
import { TABLE_HEADERS, EMPTY_CELL_VALUE } from "consts";
import { DocumentDTO } from "api";
import { Link } from "react-router-dom";
import { AttachedDocsButton } from "./components/docs-button";
import { UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "../document-summary-v2/utility-bill/utility-bill-allDataEntries-view/consts";

export const DOCUMENT_UPLOAD_PAGE = {
  ABSOLUTE_PATH: "/documents",
  PATH: "",
  TITLE: "Documents",
  ICON: <EsgFileIcon />,
  renderMenu: true,
};

export const ADD_DOCUMENT_BILL_PAGE = {
  TITLE: "Add document bill",
  ABSOLUTE_PATH: (documentId: string) =>
    `${DOCUMENT_UPLOAD_PAGE.ABSOLUTE_PATH}/${documentId}/add`,
  PATH: ":documentId/add",
  KEY: "documentId",
} as const;

export const BASE_DATA_ENTRIES = [
  {
    label: "Warning (Conversion Error)",
    name: "Warning (Conversion Error)",
    value: 1,
  },
  {
    label: "Warning (Missing Fields)",
    name: "Warning (Missing Fields)",
    value: 2,
  },
  {
    label: "Warning (Potential Error Identified)",
    name: "Warning (Potential Error Identified)",
    value: 3,
  },
  {
    label: "Emissions Included (AI Generated)",
    name: "Emissions Included (AI Generated)",
    value: 4,
  },
  {
    label: "Emissions Included (User Confirmed)",
    name: "Emissions Included (User Confirmed)",
    value: 5,
  },
  {
    label: "Emissions Included (Excel Upload)",
    name: "Emissions Included (Excel Upload)",
    value: 7,
  },
  {
    label: "Approved (Signed Off)",
    name: "Approved (Signed Off)",
    value: 8,
  },
  {
    label: "Approved (Locked)",
    name: "Approved (Locked)",
    value: 9,
  },
];

export const ACTIVE_FLOW_DATA_ENTRIES = [
  {
    label: "Reviewed",
    name: "Reviewed",
    value: 6,
  },
];

export const ACTIVE_ADDITIONAL_STATUS_DATA_ENTRIES = [
  {
    label: "Not Applicable (Invalid Data)",
    name: "Not Applicable (Invalid Data)",
    value: 10,
  },
  {
    label: "Not Applicable (Cost Only)",
    name: "Not Applicable (Cost Only)",
    value: 11,
  },
];

export const DATA_ENTRIES_STATUS_PRIORITY_MAP: Record<string, number> = {
  "Warning (Conversion Error)": 0,
  "Warning (Missing Fields)": 1,
  "Warning (Potential Error Identified)": 2,
  "Emissions Included (AI Generated)": 3,
  "Emissions Included (User Confirmed)": 4,
  "Approved (Signed Off)": 5,
  "Approved (Locked)": 6,
  Reviewed: 7,
  "Emissions Included (Excel Upload)": 8,
  "Not Applicable (Invalid Data)": 9,
  "Not Applicable (Cost Only)": 10,
};

export const getDataUploadColumns = ({
  handleRowClick,
}: {
  handleRowClick: (row: any) => void;
}): MRT_ColumnDef<any>[] => [
  {
    id: "newTab",
    header: "",
    size: 20,
    accessorKey: "newTab",
    enableColumnActions: false,
    enableColumnFilter: false,
    enableResizing: false,
    Cell: ({ row }: any) => {
      const link =
        row?.original?.bills !== null && row?.original?.bills?.length > 0
          ? UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(
              row?.original?.bills[0]?.id,
            )
          : ADD_DOCUMENT_BILL_PAGE.ABSOLUTE_PATH(row?.original?.id);

      return (
        <Tooltip title="Open bill in a new tab" placement="top">
          <Link
            to={`${link}`}
            target="_blank"
            style={{
              maxWidth: "20px",
              padding: "0px",
            }}
          >
            <Button
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minWidth: "20px",
                maxWidth: "20px",
                padding: "0px",
                "& span": {
                  width: "0px",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(row);
              }}
            >
              <NewTabIcon />
            </Button>
          </Link>
        </Tooltip>
      );
    },
  },
  {
    header: "Data entries status",
    Header: <Box>Data Entries Status</Box>,
    accessorKey: "bills",
    muiTableHeadCellColumnActionsButtonProps: { "tour-step": "12" } as any,
    muiTableHeadCellProps: { "tour-step": "13" } as any,
    filterFn: (row: any, id: any, filterValue: any) => {
      const status = row?.original?.dateEntryStatus;
      return status.toLowerCase().includes(filterValue.toLowerCase());
    },
    Cell: ({ row }: any) => {
      const status = row?.original?.dateEntryStatus;
      const isInProcess = row?.original?.status === "PROCESSING";
      const missingFields = row?.original?.missingFields || [];
      const errorMessage = row?.original?.errorMessage;
      const priority = DATA_ENTRIES_STATUS_PRIORITY_MAP[status] ?? 9;

      const getTooltipMessage = () => {
        switch (priority) {
          case 0:
            return "There was an error converting the bill information to emissions data";
          case 1:
            return missingFields.length > 0 ? (
              <div style={{ textAlign: "left" }}>
                {missingFields.map((t: string) => (
                  <div key={`key-${t}`}>• {t}</div>
                ))}
              </div>
            ) : (
              ""
            );
          case 2:
            return errorMessage ? (
              <span style={{ whiteSpace: "pre-line" }}>{errorMessage}</span>
            ) : (
              ""
            );
          case 3:
            return "Associated emissions from 100% AI extraction have been included";
          case 4:
            return "Associated emissions have been included following user confirmation of a warning message";
          case 5:
          case 6:
            return "This bill is for a site and time period that has been signed off or locked";
          default:
            return "";
        }
      };

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            paddingLeft: "4px",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Tooltip title={getTooltipMessage()}>
            <span style={{ position: "relative" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 5 5"
                fill={getDotColor(priority)}
                style={{ marginRight: "9px" }}
              >
                <circle cx="2.5" cy="2.5" r="2.5" />
              </svg>
              {isInProcess
                ? "Processing"
                : getStatusLabel(status, missingFields)}
            </span>
          </Tooltip>
        </Box>
      );
    },
    size: 300,
  },
  {
    header: "File name",
    Header: "File name",
    accessorKey: "fileName",
    muiTableHeadCellProps: { "tour-step": "14" } as any,
    Cell: ({ cell }: any) => {
      const value = cell?.getValue() as string;
      return (
        <Tooltip title={value}>
          <Box
            sx={{
              display: "block", // Ensure it's a block-level element
              width: "200px", // Set a fixed width
              overflow: "hidden",
              textOverflow: "ellipsis", // Show ellipsis if the text overflows
            }}
          >
            {value}
          </Box>
        </Tooltip>
      );
    },
    size: 230,
  },
  {
    header: "Site",
    accessorKey: "site",
    muiTableHeadCellProps: { "tour-step": "15" } as any,
    Cell: ({ cell }: any) => {
      const value = cell?.getValue() as string;
      return (
        <Tooltip title={value}>
          <Box
            sx={{
              display: "block", // Ensure it's a block-level element
              width: "180px", // Set a fixed width
              overflow: "hidden",
              textOverflow: "ellipsis", // Show ellipsis if the text overflows
            }}
          >
            {value}
          </Box>
        </Tooltip>
      );
    },
    size: 120,
  },
  {
    header: "Utility Type",
    accessorKey: "utilityType",
    muiTableHeadCellProps: { "tour-step": "16" } as any,
    Cell: ({ cell }: any) => {
      const value = cell?.getValue() as string;

      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }

      return (
        <Tooltip title={value}>
          <Box
            sx={{
              paddingLeft: "12px",
              alignItems: "center",
              Width: "30px",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {value}
          </Box>
        </Tooltip>
      );
    },
    size: 140,
  },
  {
    accessorFn: (row: any) =>
      `${row?.uploader?.firstName} ${row?.uploader?.lastName}`,
    header: TABLE_HEADERS.uploader,
    Header: <Box sx={{ paddingLeft: "14px" }}>Uploader</Box>,
    id: "uploader",
    muiTableHeadCellProps: { "tour-step": "17" } as any,
    filterFn: (row: any, columnId: any, filterValue: any) => {
      const uploader = row.original?.uploader;
      const fullName = `${uploader?.firstName || ""} ${
        uploader?.lastName || ""
      }`.toLowerCase();
      const email = uploader?.email?.toLowerCase() || "";
      const search = filterValue.toLowerCase();

      return fullName.includes(search) || email.includes(search);
    },
    Cell: ({ cell, row }: any) => {
      const value = cell?.getValue();
      if (!value) {
        return EMPTY_CELL_VALUE;
      }
      return (
        <Tooltip title={row?.original?.uploader?.email}>
          <Box sx={{ paddingLeft: "14px" }}>
            <Avatar sx={{ width: "34px", height: "34px" }}>
              {getInitials(value)}
            </Avatar>
          </Box>
        </Tooltip>
      );
    },
    size: 150,
  },
  {
    header: "Uploaded at",
    accessorKey: "uploadDate",
    muiTableHeadCellProps: { "tour-step": "18" } as any,
    Cell: ({ cell }: any) => {
      const date: string = cell?.getValue() as string;
      const time = dayjs(date)?.utc()?.local()?.format("HH:mm");
      const value = dayjs(date)?.utc()?.local()?.format(getDateFormatset());
      if (isNullOrUndefined(value)) {
        return EMPTY_CELL_VALUE;
      }

      return <Box>{`${value}, ${time}`}</Box>;
    },
    size: 160,
  },
  {
    id: "dataEntries",
    header: "Data entries",
    Header: <Box>Data Entries</Box>,
    muiTableHeadCellProps: { "tour-step": "19" } as any,
    Cell: ({ cell }: any) => {
      const value = cell?.getValue() as DocumentDTO["pages"];

      return (
        <Box sx={{ textAlign: "center", paddingLeft: "5px" }}>
          {value || EMPTY_CELL_VALUE}
        </Box>
      );
    },
    accessorFn: (originalRow: any) =>
      originalRow?.bills?.length === null ||
      originalRow?.bills?.length === undefined
        ? "-"
        : originalRow?.bills?.length,
    size: 140,
  },
  {
    id: "supportingDocs",
    header: "Docs",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    muiTableBodyCellProps: {
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
    },
    Cell: ({ row }: any) => (
      <AttachedDocsButton
        documentId={String(row.original.id)}
        documentCount={row.original.supportingFilesCount ?? 0}
      />
    ),
  },
  {
    header: "Pages",
    Header: <Box>Pages</Box>,
    accessorKey: "pages",
    muiTableHeadCellProps: { "tour-step": "20" } as any,
    Cell: ({ cell }: any) => {
      const value = cell?.getValue() as DocumentDTO["pages"];

      return (
        <Box sx={{ textAlign: "center", paddingLeft: "5px" }}>
          {value || EMPTY_CELL_VALUE}
        </Box>
      );
    },
    size: 100,
  },
  {
    header: "AI Extraction",
    Header: <Box>AI Extraction</Box>,
    accessorKey: "aiExtraction",
    muiTableHeadCellProps: { "tour-step": "21" } as any,
    filterFn: (row: any, columnId: any, filterValue: any) => {
      const value = row?.original?.aiExtraction;

      const normalized = String(filterValue)
        .trim()
        .toLowerCase()
        .replace("%", "");

      if (normalized === "na") {
        return value === null || value === undefined;
      }

      return String(value) === String(normalized).trim();
    },
    Cell: ({ cell }: any) => {
      const value = cell?.getValue() as number;
      const isExcel = cell?.row?.original?.excel;

      return (
        <Box
          sx={{
            width: "100px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {isNullOrUndefined(value) || isExcel ? (
            <>
              <LinearProgress
                sx={{
                  minWidth: "90px",
                  height: "8px",
                  borderRadius: "5px",
                  border: "2px solid #DCDCDC",
                  backgroundColor: "#f3f3f3",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#DCDCDC",
                  },
                }}
                variant="determinate"
                value={100}
              />
              <p style={{ paddingLeft: "4px", fontSize: "12px" }}>N/A</p>
            </>
          ) : (
            <>
              <LinearProgress
                sx={{
                  minWidth: "90px",
                  height: "8px",
                  borderRadius: "5px",
                  border: `1px solid ${COLORS.Mainblue}`,
                  backgroundColor: "#f3f3f3",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: COLORS.Mainblue,
                  },
                }}
                variant="determinate"
                value={value}
              />
              <p
                style={{ paddingLeft: "4px", fontSize: "12px" }}
              >{`${value}%`}</p>
            </>
          )}
        </Box>
      );
    },
    size: 150,
  },
  {
    header: "AI Algorithm",
    accessorKey: "aiAlgorithm",
    muiTableHeadCellProps: { "tour-step": "22" } as any,
    Cell: ({ cell }: any) => {
      const row = cell.row.original;
      const value = determineAlgorithmName(row);

      return (
        <Tooltip title={value}>
          <Box
            sx={{
              paddingLeft: "12px",
              alignItems: "center",
              Width: "30px",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {value}
          </Box>
        </Tooltip>
      );
    },
    size: 200,
    filterFn: (row: any, id: any, filterValue: any) => {
      const algorithmName = determineAlgorithmName(row.original);
      return algorithmName.toLowerCase().includes(filterValue.toLowerCase());
    },
  },
];

export const ONBOARDING_GUIDE_STEPS: Step[] = [
  {
    target: "body",
    placement: "center",
    title: "Welcome to Documents Upload",
    content:
      "This is where utility bills and supporting documents land before they become emissions data. We'll walk through the key controls — about 90 seconds.",
  },
  {
    target: "[tour-step='2']",
    title: "Upload your documents",
    content:
      "Drop in bills, invoices, or PDFs — single files or in bulk. Our AI parses them in the background, so you can keep working while it runs.",
  },
  {
    target: "[tour-step='3']",
    title: "Start month",
    content:
      "Filters to bills uploaded in this month. Defaults to the current month — adjust to look at older uploads.",
  },
  {
    target: "[tour-step='4']",
    title: "End month",
    content:
      "The end of the upload date range. Keep it the same as start month for a single month, or extend it to span a longer window.",
  },
  {
    target: "[tour-step='5']",
    title: "Filter by site",
    content:
      "Narrow to specific facilities or regions. Leave on All Selected for a full portfolio view.",
  },
  {
    target: "[tour-step='6']",
    title: "Filter by data entry status",
    content:
      "Slice documents by where they sit in the workflow — warning, reviewed, approved, signed off, or locked.",
  },
  {
    target: "[tour-step='7']",
    title: "Show only unmoved",
    content:
      "Toggle on to find documents whose data hasn't been pushed into emissions yet — a fast way to catch stragglers before close.",
  },
  {
    target: "[tour-step='8']",
    title: "Apply your filters",
    content:
      "Search refreshes the table with whatever filter combination you've set above.",
  },
  {
    target: "[tour-step='9']",
    title: "Reset to defaults",
    content: "One click to clear every filter and start fresh.",
  },
  {
    target: "[tour-step='10']",
    placement: "left",
    title: "Track period completeness",
    content:
      "Snapshot of completeness of the uploaded data, each bar shows the share of this period's data in each status. Warning flags issues to resolve; Emissions Included, Signed Off and Locked, grow as bills move through the workflow. Hover any bar for the exact entry count.",
  },
  {
    target: "[tour-step='11']",
    placement: "top",
    title: "Click a row for details",
    content:
      "Click any row to open a side panel with the original document and all of its extracted data entries — review, edit, or push it through the workflow from there.",
  },
  {
    target: "[tour-step='12']",
    title: "Sort, filter, or hide any column",
    content:
      "Click the ⋮ next to any column header for column-level controls: sort ascending or descending, filter by the column's values, or hide the column. For text filters, type your value and press Return to apply. Use Show all columns to bring hidden ones back.",
  },
  {
    target: "[tour-step='13']",
    title: "Data Entries Status",
    content:
      "Shows where each entry sits in the workflow: Warning → Emissions Included → Approved → Signed Off → Locked. Green means emissions have been calculated for the entry. Red means the entry has a warning and needs review before it can proceed. Filter by this to find entries that need attention.",
  },
  {
    target: "[tour-step='14']",
    title: "File name",
    content:
      "The original filename of the uploaded document. Click any row to open the document viewer side-by-side with the extracted data.",
  },
  {
    target: "[tour-step='15']",
    title: "Site",
    content: "Which facility the document belongs to.",
  },
  {
    target: "[tour-step='16']",
    title: "Utility Type",
    content: "The utility type — electricity, natural gas, water, fuel, etc.",
  },
  {
    target: "[tour-step='17']",
    title: "Uploader",
    content:
      "Who uploaded the document. Useful when chasing down a missing bill or auditing where data came from.",
  },
  {
    target: "[tour-step='18']",
    title: "Uploaded at",
    content:
      "Timestamp of when the document was uploaded. This is what the Start / End Month filters at the top of the page filter on.",
  },
  {
    target: "[tour-step='19']",
    title: "Data Entries",
    content:
      "How many data points we extracted from the document — there may be multiple utility invoices in one document.",
  },
  {
    target: "[tour-step='20']",
    title: "Pages",
    content: "Page count of the source document.",
  },
  {
    target: "[tour-step='21']",
    title: "AI Extraction",
    content:
      "Percentage of required fields automatically extracted by AI from the uploaded document.",
  },
  {
    target: "[tour-step='22']",
    title: "AI Algorithm",
    content:
      "Which extraction model was used. Different document types route to different algorithms",
  },
  {
    target: "[tour-step='23']",
    placement: "right",
    title: "Take action on multiple rows",
    content:
      "Tick one or more rows and the buttons here become active —download or delete documents in one go.",
  },
  {
    target: "[tour-step='24']",
    placement: "right",
    title: "You're all set",
    content:
      "Restart this tour anytime from the Help menu or the Start Tour pill near the page title. Now let's get those emissions counted.",
  },
];
